import { chargeableUnits, Rational } from '@hpd/domain';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import type { FormEvent } from 'react';

import { productsQuery } from '../api/inventory.ts';
import { productRatesQuery, rateStructuresQuery, toDefinition } from '../api/pricing.ts';
import {
  addLine,
  dayLabel,
  quoteQuery,
  quotesQuery,
  taxCodesQuery,
  updateLine,
  type LineDiscountInput,
  type Quote,
  type QuoteLine,
} from '../api/quotes.ts';
import { Button } from '../components/button.tsx';
import { Field } from '../components/field.tsx';
import { RoomPicker, roomForLine } from './produced-event.tsx';
import { dollarsToMinorUnits, minorUnitsToDollars } from '../money.ts';

// Adding or changing one quote line (CB-REQ QUO-01, PRC-13, PRC-05 at v1.943). The server prices it
// — this form only says what is being hired, for how many, and on which days — so the figure on the
// quote is always the API's, computed by the same function the price check uses.

export function LineForm({
  quote,
  line,
  seesPrices,
  startWith,
  onDone,
}: {
  quote: Quote;
  line?: QuoteLine;
  seesPrices: boolean;
  /**
   * A new line already chosen in the item picker that needs more than a quantity: its hours or
   * kilometres. `unit` comes from the API's measure-required refusal, so it is known even to someone
   * who cannot see the rates (INV-10).
   */
  startWith?: { productId: string; quantity: number; roomId: string | null; unit: 'hour' | 'km' };
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const products = useQuery(productsQuery);
  const taxCodes = useQuery(taxCodesQuery);
  const [productId, setProductId] = useState(line?.productId ?? startWith?.productId ?? '');
  const [quantity, setQuantity] = useState(String(line?.quantity ?? startWith?.quantity ?? 1));
  const windowDays = quote.soldWindow.days;
  const [choosing, setChoosing] = useState(line?.chargeableDays != null);
  const [days, setDays] = useState<string[]>(line?.chargeableDays ?? windowDays);
  const [measure, setMeasure] = useState(
    line?.enteredMeasure
      ? String(line.enteredMeasure.numerator / line.enteredMeasure.denominator)
      : '',
  );
  const [discountKind, setDiscountKind] = useState<'none' | 'percent' | 'amount'>(
    line?.discount?.kind ?? 'none',
  );
  const [discountValue, setDiscountValue] = useState(() => {
    const d = line?.discount;
    if (!d) return '';
    if (d.kind === 'amount' && d.amount) return minorUnitsToDollars(d.amount.amountMinor);
    if (d.kind === 'percent' && d.fraction) {
      return String((d.fraction.numerator * 100) / d.fraction.denominator);
    }
    return '';
  });
  const [discountReason, setDiscountReason] = useState(line?.discount?.reason ?? '');
  const [startingDiscount] = useState(discountValue);
  const [taxCodeId, setTaxCodeId] = useState('');
  // JOB-14 at v1.958: a produced event's line may go to a room of its venue.
  const [roomId, setRoomId] = useState<string | null>(line?.room?.id ?? startWith?.roomId ?? null);
  const [unaddedRoom, setUnaddedRoom] = useState<string | null>(null);
  const roomsApply = quote.type === 'produced_event' && quote.venue !== null;
  const [localError, setLocalError] = useState<string | null>(null);

  // Which unit the chosen product is charged in decides the question asked: days, or a measure.
  // Read from its rate, which only someone above the price line can see; without it, the server
  // still refuses a wrong shape with a sentence.
  const rates = useQuery({ ...productRatesQuery(productId), enabled: seesPrices && !!productId });
  const structures = useQuery({ ...rateStructuresQuery, enabled: seesPrices });
  const groupRate = rates.data?.find((r) => r.priceGroup.id === quote.priceGroup.id)?.rate;
  const structure = structures.data?.find((s) => s.id === groupRate?.rateStructure.id);
  const unit = line ? line.unit : (structure?.unit ?? startWith?.unit ?? 'day');
  const byTheDay = unit === 'day';
  // INV-A14 at v1.982: a venue's own equipment is included at no charge, on no rate of its own.
  const chosen = products.data?.find((p) => p.id === productId);
  const venueOwned = !!chosen?.ownerVenueId;
  const isFixed = venueOwned || structure?.kind === 'fixed';

  const save = useMutation({
    mutationFn: async () => {
      const discount: LineDiscountInput | null =
        discountKind === 'percent'
          ? { kind: 'percent', percent: discountValue.trim() }
          : discountKind === 'amount'
            ? { kind: 'amount', amountMinor: dollarsToMinorUnits(discountValue) ?? '' }
            : null;
      const body = {
        quantity: Number(quantity),
        ...(byTheDay && !isFixed ? { chargeableDays: choosing ? days : null } : {}),
        ...(!byTheDay && !isFixed ? { measure: measure.trim() || null } : {}),
        ...(seesPrices ? { discount } : {}),
        ...(discount ? { discountReason: discountReason.trim() } : {}),
        ...(taxCodeId ? { taxCodeId } : {}),
      };
      const chosenRoomId = roomsApply
        ? await roomForLine(queryClient, quote.venue!.id, roomId, unaddedRoom)
        : roomId;
      const room = roomsApply ? { roomId: chosenRoomId } : {};
      if (!line) return addLine(quote.id, { productId, ...body, ...room });
      // A room is not a price: a line only moved to another room is not repriced, so a line the
      // client agreed on a revision keeps its price (PRC-08 at v1.950).
      const priced =
        body.quantity !== line.quantity ||
        JSON.stringify(body.chargeableDays ?? null) !== JSON.stringify(line.chargeableDays) ||
        (body.measure ?? null) !==
          (line.enteredMeasure
            ? String(line.enteredMeasure.numerator / line.enteredMeasure.denominator)
            : null) ||
        discountKind !== (line.discount?.kind ?? 'none') ||
        discountValue !== startingDiscount ||
        (discount !== null && discountReason.trim() !== line.discount?.reason) ||
        taxCodeId !== '';
      if (!priced && roomsApply) return updateLine(quote.id, line.id, { roomId: chosenRoomId });
      return updateLine(quote.id, line.id, { ...body, ...room });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: quoteQuery(quote.id).queryKey });
      await queryClient.invalidateQueries({ queryKey: quotesQuery.queryKey });
      onDone();
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    setLocalError(null);
    if (!line && !productId) return setLocalError('Choose what is being hired.');
    if (!Number.isInteger(Number(quantity)) || Number(quantity) < 1) {
      return setLocalError('The quantity is a whole number, 1 or more.');
    }
    if (choosing && byTheDay && days.length === 0) {
      return setLocalError('Tick at least one day, or charge every day of the hire.');
    }
    if (discountKind === 'amount' && dollarsToMinorUnits(discountValue) === null) {
      return setLocalError('The discount should be an amount in dollars, like 50 or 49.50.');
    }
    if (discountKind !== 'none' && !discountReason.trim()) {
      return setLocalError('A discount needs a reason; someone will ask about it later.');
    }
    save.mutate();
  }

  // PRC-13: on a plateau, clearing days may not change the charge at all. Arithmetically right,
  // and it will still look broken to whoever cleared the boxes, so say so before they save.
  let plateau: string | null = null;
  if (structure && byTheDay && choosing && days.length > 0 && days.length < windowDays.length) {
    const def = toDefinition(structure);
    const some = chargeableUnits(def, Rational.fromInt(days.length));
    const all = chargeableUnits(def, Rational.fromInt(windowDays.length));
    if (some.equals(all)) {
      plateau = `On ${structure.name}, ${days.length} days charges the same as all ${windowDays.length}: clearing these days does not reduce the price. A discount does.`;
    }
  }

  const error = localError ?? (save.isError ? save.error.message : null);

  return (
    <form onSubmit={submit} className="panel flex flex-col gap-4">
      <h3 className="section-title">{line ? `Change ${line.description}` : 'Add a line'}</h3>
      {error ? (
        <p role="alert" className="alert">
          {error}
        </p>
      ) : null}

      {line ? null : (
        <div className="flex flex-col gap-1.5">
          <label htmlFor="line-product" className="text-xs text-ink-soft">
            Item or service
          </label>
          <select
            id="line-product"
            className="input"
            value={productId}
            onChange={(e) => setProductId(e.currentTarget.value)}
          >
            <option value="">Choose…</option>
            {/* A venue's own equipment only as its hire includes it here (INV-A14 at v1.983). */}
            {(products.data ?? [])
              .filter(
                (p) =>
                  !p.ownerVenueId ||
                  quote.inclusions.some((i) => i.productId === p.id && !i.declined),
              )
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.tracking === 'service' ? ' (service)' : ''}
                  {p.ownerVenueId ? ' (the venue’s own, included)' : ''}
                </option>
              ))}
          </select>
          {/* Said as soon as it is chosen, not after Add line: an unpriced product can't go on. */}
          {productId && rates.isSuccess && !groupRate && !venueOwned ? (
            <p role="status" className="text-sm text-accent-2-700">
              This has no hire rate for {quote.priceGroup.name} yet, so it can’t be priced.{' '}
              <Link to="/inventory/$productId" params={{ productId }} className="link">
                Set rate
              </Link>
              .
            </p>
          ) : null}
        </div>
      )}

      {roomsApply ? (
        <RoomPicker
          venueId={quote.venue!.id}
          value={roomId}
          onChange={setRoomId}
          onNaming={setUnaddedRoom}
        />
      ) : quote.type === 'produced_event' ? (
        <p className="text-sm text-ink-soft">Choose the venue to put lines in its rooms.</p>
      ) : null}

      <Field
        label="Quantity"
        type="number"
        min={1}
        required
        value={quantity}
        onChange={(e) => setQuantity(e.currentTarget.value)}
      />

      {venueOwned ? (
        <p className="text-sm text-ink-soft">
          The venue’s own: included at no charge, and not packed.
        </p>
      ) : isFixed ? (
        <p className="text-sm text-ink-soft">One charge, however long the hire.</p>
      ) : byTheDay ? (
        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm font-medium text-ink">Days charged</legend>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="days-charged"
              value="every"
              checked={!choosing}
              onChange={() => setChoosing(false)}
            />
            All days ({windowDays.length})
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="days-charged"
              value="chosen"
              checked={choosing}
              onChange={() => setChoosing(true)}
            />
            Pick days
          </label>
          {choosing ? (
            <div className="flex flex-wrap gap-2 pl-6">
              {windowDays.map((d) => (
                <label
                  key={d}
                  className="flex items-center gap-1.5 border border-divider px-2 py-1 text-sm"
                >
                  <input
                    type="checkbox"
                    checked={days.includes(d)}
                    onChange={(e) =>
                      setDays(
                        e.currentTarget.checked ? [...days, d].sort() : days.filter((x) => x !== d),
                      )
                    }
                  />
                  {dayLabel(d)}
                </label>
              ))}
            </div>
          ) : null}
          {plateau ? (
            <p role="status" className="notice">
              {plateau}
            </p>
          ) : null}
        </fieldset>
      ) : (
        <Field
          label={unit === 'hour' ? 'Hours' : 'Kilometres'}
          hint={
            unit === 'hour' ? 'Rounded up to the half hour, with the minimum applied.' : undefined
          }
          inputMode="decimal"
          required
          value={measure}
          onChange={(e) => setMeasure(e.currentTarget.value)}
        />
      )}

      {seesPrices ? (
        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm font-medium text-ink">Discount</legend>
          <div className="flex flex-wrap gap-4">
            {(
              [
                ['none', 'None'],
                ['percent', 'Percentage'],
                ['amount', 'Amount'],
              ] as const
            ).map(([value, label]) => (
              <label key={value} className="flex items-center gap-2">
                <input
                  type="radio"
                  name="discount-kind"
                  value={value}
                  checked={discountKind === value}
                  onChange={() => setDiscountKind(value)}
                />
                {label}
              </label>
            ))}
          </div>
          {discountKind !== 'none' ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label={discountKind === 'percent' ? 'Percent off' : 'Dollars off, ex GST'}
                inputMode="decimal"
                required
                value={discountValue}
                onChange={(e) => setDiscountValue(e.currentTarget.value)}
              />
              <Field
                label="Why"
                required
                value={discountReason}
                onChange={(e) => setDiscountReason(e.currentTarget.value)}
              />
            </div>
          ) : null}
        </fieldset>
      ) : null}

      {(taxCodes.data?.length ?? 0) > 1 ? (
        <div className="flex flex-col gap-1.5">
          <label htmlFor="line-tax" className="text-xs text-ink-soft">
            Tax
          </label>
          <select
            id="line-tax"
            className="input"
            value={taxCodeId}
            onChange={(e) => setTaxCodeId(e.currentTarget.value)}
          >
            <option value="">{line ? `Keep ${line.taxCode}` : 'Default'}</option>
            {(taxCodes.data ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      <div className="flex gap-3">
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Pricing…' : line ? 'Save' : 'Add'}
        </Button>
        <Button type="button" variant="quiet" onClick={onDone} disabled={save.isPending}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
